<template>
  <div class="action_tgl_box">
    <!-- 1. 映射关系看板 (Source -> Target) -->
    <div class="tgl_mapping_board">
      <div class="board_inner">
        <!-- 当前按键 (只读) -->
        <div class="key_slot source">
          <div class="slot_label">{{ $t('current_selected_key') }}</div>
          <div class="key_display active">{{ currentKey }}</div>
        </div>

        <!-- 连动装饰 -->
        <div class="mapping_flow">
          <div class="flow_icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M13 17l5-5-5-5M6 17l5-5-5-5" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="flow_label">TGL</div>
        </div>

        <!-- 目标按键 (可点击更改) -->
        <trigger_btn class="key_slot target" :label="$t('mapping_action')" :vks="Keys[0]?.KeyCode || []" :tab="tab" :show-edit="true" :large="true" @trigger-click="showKeyDialog = true" />
      </div>
    </div>

    <!-- 2. 模式选项区 -->
    <div class="modes_section" v-if="specialCheckCurrent(speciaBehiver.DeviceOfRoyuan) || specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen)">
      <div class="modes_group">
        <div class="mode_card_item" :class="{ active: TGLMode == TriggerType.continuous }" @click="changeMode(TriggerType.continuous)">
          <div class="mode_main_content">
            <div class="mode_icon_box continuous">
              <div class="dot_indicator"></div>
            </div>
            <div class="mode_info_text">
              <div class="mode_name">{{ $t('continuous_trigger') }}</div>
              <div class="mode_desc">{{ $t('continuous_trigger_desc') }}</div>
            </div>
          </div>
          <div class="mode_radio_indicator">
            <div class="radio_circle"></div>
          </div>
        </div>

        <div class="mode_card_item" :class="{ active: TGLMode == TriggerType.long_press }" @click="changeMode(TriggerType.long_press)">
          <div class="mode_main_content">
            <div class="mode_icon_box long_press">
              <div class="dot_indicator"></div>
            </div>
            <div class="mode_info_text">
              <div class="mode_name">{{ $t('long_press_to_trigger') }}</div>
              <div class="mode_desc">{{ $t('long_press_to_trigger_desc') }}</div>
            </div>
          </div>
          <div class="mode_radio_indicator">
            <div class="radio_circle"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 按键选择弹窗 -->
    <action_key_dialog v-model:visible="showKeyDialog" :row-index="0" :base-tab="tab" :selected-keys="Keys[0]?.KeyCode || []" @confirm="onKeyConfirm" />
  </div>
</template>

<script lang="ts" setup>
import { VK, kks } from '@/define/define'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { BaseTabs, SetType, TriggerType, actionSet } from '@/service/driver/action_set'
import { MagnetSeniorType } from '@libs/types/enum'
import { onMounted, ref, toRefs, computed, watch } from 'vue'
import trigger_btn from './trigger_btn.vue'
import action_key_dialog from './action_key_dialog.vue'
import { normalizeKeyCode } from '@/tool/utility'
import { operate } from '@/service/driver/operate'
import { currentSelectedKeyText, getVkText } from './action_config'

const props = defineProps<{ tab: BaseTabs; keys: VK[]; type: SetType }>()
const emit = defineEmits(['changeSelectedKeys'])

let { Keys, KeyType, TGLMode } = toRefs(actionSet.magnetValue)
const showKeyDialog = ref(false)

const currentKey = computed(() => currentSelectedKeyText.value)

const selectedKey = computed(() => {
  const code = Keys.value[0]?.KeyCode?.[2] || 0
  return getVkText(code)
})

const hasTargetKey = computed(() => {
  return Keys.value[0] && Keys.value[0].KeyCode.filter((e) => e > 0).length > 0
})

const initData = () => {
  if (Keys.value.length != 1) {
    Keys.value = [
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [],
        Travel: 0,
      },
    ]
  }
  if (KeyType.value != MagnetSeniorType.SeniorKeyTypeTGL) {
    KeyType.value = MagnetSeniorType.SeniorKeyTypeTGL
  }
}

onMounted(() => {
  initData()
})

watch(
  () => JSON.stringify(actionSet.magnetValue),
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      initData()
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => operate.currentKey.value,
  () => {
    initData()
  },
)

const onKeyConfirm = () => {
  const keys = normalizeKeyCode(actionSet.baseKeys.selectedKeys, actionSet.baseKeys.baseTab)
  if (Keys.value[0]) {
    Keys.value[0].KeyCode = keys
  }
}

const changeMode = (value: TriggerType) => {
  TGLMode.value = value
}
</script>

<style scoped lang="less">
.action_tgl_box {
  margin: auto;
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;

  /* 映射关系看板 - 呼吸感毛玻璃设计 */
  .tgl_mapping_board {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(20px);
    border-radius: var(--radius-l);
    padding: 14px 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.8);
    position: relative;
    /* overflow: hidden; */

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 100%);
      pointer-events: none;
      border-radius: var(--radius-l); /* 手动对齐父容器圆角 */
    }

    .board_inner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20px;
      position: relative;
      z-index: 1;
    }

    .key_slot {
      width: 130px;
      height: 72px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 10px 12px;
      border-radius: var(--radius-l);
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(0, 0, 0, 0.03);
      transition: var(--transition-base);

      .slot_label {
        font-size: 11px;
        color: var(--color-text-hint);
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 2px;
      }

      .key_display {
        font-size: 20px;
        font-weight: 900;
        color: var(--color-dark);
        line-height: 1.1;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          color: var(--brand-color-pure);
        }
      }

      &.target {
        cursor: pointer;
        position: relative;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
        padding: 0;
        height: 72px;

        &:hover {
          border-color: var(--brand-color-pure);
          box-shadow: var(--action-btn-shadow-hover);
          transform: translateY(-3px);
        }

        &.empty {
          .key_display {
            font-size: 14px;
            color: var(--color-text-hint);
            font-weight: 600;
          }
        }

        .edit_hint {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 14px;
          height: 14px;
          color: var(--color-text-hint);
          opacity: 0.4;
        }
      }
    }

    .mapping_flow {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;

      .flow_icon {
        width: 20px;
        height: 20px;
        color: var(--brand-color-pure);
        filter: drop-shadow(0 0 8px rgba(255, 124, 0, 0.3));
      }
      .flow_label {
        font-size: 9px;
        font-weight: 900;
        color: var(--brand-color-pure);
        letter-spacing: 2px;
        opacity: 0.7;
      }
    }
  }

  /* 模式选择 - 现代化网格卡片 */
  .modes_section {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .section_header {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0 8px;

      .title {
        font-size: 12px;
        font-weight: 700;
        color: var(--color-text-hint);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .divider {
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0) 100%);
      }
    }

    .modes_group {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }

    .mode_card_item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      background: #ffffff;
      border-radius: var(--radius-m);
      border: 1px solid rgba(0, 0, 0, 0.03);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
      cursor: pointer;
      transition: var(--transition-base);
      height: 72px;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
        border-color: rgba(0, 0, 0, 0.08);
      }

      &.active {
        background: #fff;
        border-color: var(--brand-color-pure);
        box-shadow: 0 10px 30px rgba(255, 124, 0, 0.12);

        .mode_info_text .mode_name {
          color: var(--brand-color-pure);
        }
        .mode_radio_indicator {
          border-color: var(--brand-color-pure);
          .radio_circle {
            transform: scale(1);
          }
        }
      }

      .mode_main_content {
        display: flex;
        align-items: center;
        gap: 16px;
        overflow: hidden;
      }

      .mode_icon_box {
        width: 36px;
        height: 36px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &.continuous {
          background: rgba(0, 150, 255, 0.1);
          .dot_indicator {
            background: #0096ff;
            box-shadow: 0 0 8px rgba(0, 150, 255, 0.4);
          }
        }
        &.long_press {
          background: rgba(255, 124, 0, 0.1);
          .dot_indicator {
            background: #ff7c00;
            box-shadow: 0 0 8px rgba(255, 124, 0, 0.4);
          }
        }

        .dot_indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
      }

      .mode_info_text {
        overflow: hidden;
        .mode_name {
          font-size: 15px;
          font-weight: 800;
          color: var(--color-dark);
          transition: color 0.3s;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .mode_desc {
          font-size: 11px;
          color: var(--color-text-hint);
          margin-top: 2px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }

      .mode_radio_indicator {
        width: 20px;
        height: 20px;
        border: 2px solid #edeff2;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: var(--transition-base);
        flex-shrink: 0;

        .radio_circle {
          width: 10px;
          height: 10px;
          background: var(--brand-color);
          border-radius: 50%;
          transform: scale(0);
          transition: transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
      }
    }
  }
}
</style>
